import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SeoService } from '../../services/seo.service';
import { SET_RESPONSE_STATUS } from '../../services/cms-page-data.service';
import { TsPieceComponent } from '../shared/ts-piece/ts-piece.component';
import { ImageSlotComponent } from '../shared/image-slot/image-slot.component';
import {
  Solution,
  SOLUTION_BASE,
  SOLUTION_CTA_TEXT,
  findSolution,
  solutionTotal,
  solutionBreakdown,
  solutionPieces,
  SolutionImageSlot,
} from './solutions.data';

/**
 * Detalle de una solución por sector (`/soluciones/:slug`). Plantilla común
 * servida desde `solutions.data.ts` (código, no CMS): hero, desglose con los
 * mismos precios del catálogo, "un día con la solución", ejemplo de uso y CTA a
 * la prueba con la selección ya puesta. Slug desconocido → 404 real en SSR.
 */
@Component({
  selector: 'app-solucion-detalle',
  standalone: true,
  imports: [RouterLink, TsPieceComponent, ImageSlotComponent],
  templateUrl: './solucion-detalle.component.html',
  styleUrl: './solucion-detalle.component.scss',
})
export class SolucionDetalleComponent {
  private route = inject(ActivatedRoute);
  private seo = inject(SeoService);
  private setStatus = inject(SET_RESPONSE_STATUS, { optional: true });

  solution = signal<Solution | null>(null);

  readonly base = SOLUTION_BASE;
  readonly ctaText = SOLUTION_CTA_TEXT;

  constructor() {
    // paramMap emite el valor actual al suscribirse → resuelve en el primer
    // render (importante para fijar el 404 en SSR).
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(pm => {
      this.resolve(pm.get('slug') ?? '');
    });
  }

  private resolve(slug: string): void {
    const s = findSolution(slug);
    if (!s) {
      this.solution.set(null);
      this.setStatus?.(404);
      this.seo.update({
        title: 'Solución no encontrada',
        description:
          'La solución que buscas no existe o ha cambiado de dirección.',
        path: `/soluciones/${slug}`,
      });
      return;
    }
    this.solution.set(s);
    this.seo.update({
      title: s.sector,
      description: s.heroSubtitle.slice(0, 160),
      path: `/soluciones/${s.slug}`,
    });
  }

  total(s: Solution): number {
    return solutionTotal(s);
  }
  breakdown(s: Solution): string {
    return solutionBreakdown(s);
  }
  pieces(s: Solution): { color: string; outline: boolean }[] {
    return solutionPieces(s);
  }
  img(s: Solution, slot: SolutionImageSlot): string | undefined {
    return s.images[slot];
  }
  /** Color de sector para el panel de marca de los huecos sin captura. */
  brandColor(s: Solution): string {
    return s.modules[0]?.color ?? 'var(--ts-primary)';
  }
}
